import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Typography, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Snackbar, FormControlLabel, Switch, Tooltip
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DoneIcon from "@mui/icons-material/Done";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import UndoIcon from "@mui/icons-material/Undo";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [activeSubscriptions, setActiveSubscriptions] = useState({});
  const [logsOpen, setLogsOpen] = useState(false);
  const [counts, setCounts] = useState({ active: 0, pending: 0, total: 0 });
  const [searchText, setSearchText] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 🔐 Audit log state
  const [logs, setLogs] = useState([]);
  const [logsCursor, setLogsCursor] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsHasMore, setLogsHasMore] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    username: "",
    email: "",
    phone: "",
    age: "",
    gender: "",
    city: "",
    occupation: "",
    membershipType: "Free",
    approvalStatus: "Onay Bekliyor",
    isActive: true,
    isLocked: false,
    password: "",
    role: "user",
    avatar: null,
    registeredVia: "Email",
  });

  const fieldConfig = {
    name: { label: "Ad" },
    surname: { label: "Soyad" },
    username: { label: "Kullanıcı Adı" },
    email: { label: "E-posta" },
    phone: { label: "Telefon" },
    age: { label: "Yaş", type: "number", inputProps: { min: 1, max: 120 } },
    city: { label: "Şehir" },
    occupation: { label: "Meslek" }
  };

  const [preview, setPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Audit label + value formatter
  const mapActionLabel = (a) => ({
    USER_CREATE: "Kullanıcı oluşturuldu",
    USER_UPDATE: "Kullanıcı güncellendi",
    USER_DELETE: "Kullanıcı silindi",
    APPROVAL_CHANGE: "Onay durumu değişti",
    PASSWORD_RESET: "Şifre sıfırlandı",
    SUBSCRIPTION_CHANGE: "Abonelik değişti",
    LOCK: "Hesap kilitlendi",
    UNLOCK: "Hesap kilidi açıldı",
    LOGIN: "Giriş",
    LOGOUT: "Çıkış"
  }[a] || a);

  const fmtVal = (v) => {
    if (v === undefined) return "—";
    if (v === null) return "null";
    if (typeof v === "boolean") return v ? "true" : "false";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get("/api/users");
      const all = res.data;
      const onlyUsers = all.filter(u => u.role === "user");
      setUsers(onlyUsers);

      const active = onlyUsers.filter(u => u.isActive).length;
      const pending = onlyUsers.filter(u => u.approvalStatus === "Onay Bekliyor").length;
      setCounts({ active, pending, total: onlyUsers.length });
    } catch (err) {
      console.error("Kullanıcıları alırken hata:", err);
    }
  }, []);

  const fetchAllSubscriptions = async () => {
    try {
      const res = await axios.get("/api/subscriptions");
    const allSubs = res.data.data;
      const activeMap = {};
      allSubs.forEach(sub => {
        if (sub.isActive && sub.userId) {
          activeMap[sub.userId] = sub;
        }
      });
      setActiveSubscriptions(activeMap);
    } catch (err) {
      console.error("Abonelikleri alırken hata:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAllSubscriptions();
  }, [fetchUsers]);

  // 🔐 Kullanıcı geçmişi fetch
  const fetchLogs = async (userId, reset = false) => {
    try {
      setLogsLoading(true);
      const params = new URLSearchParams();
      params.set("limit", "30");
      if (!reset && logsCursor) params.set("cursor", logsCursor);

      const { data } = await axios.get(
        `/api/users/${userId}/logs?${params.toString()}`
      );

      if (reset) setLogs(data.data);
      else setLogs(prev => [...prev, ...data.data]);

      setLogsCursor(data.nextCursor);
      setLogsHasMore(Boolean(data.nextCursor));
    } catch (e) {
      console.error("Loglar alınamadı:", e);
      setSnackbar({ open: true, message: "İşlem geçmişi alınamadı", severity: "error" });
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const form = new FormData();
      for (const key in formData) {
        if (key === "avatar" && formData.avatar instanceof File) {
          form.append("avatar", formData.avatar);
        } else {
          form.append(key, formData[key]);
        }
      }

      if (!formData._id) {
        await axios.post("/api/users", form, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        await axios.put(`/api/users/${formData._id}`, form, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      fetchUsers();
      setSnackbar({ open: true, message: "Kayıt başarılı", severity: "success" });
      setOpen(false);
    } catch (err) {
      console.error("Kayıt hatası:", err);
      setSnackbar({ open: true, message: "İşlem başarısız", severity: "error" });
    }
  };

  const handleApprovalChangeByRow = async (row, status) => {
    try {
      await axios.put(`/api/users/${row._id}`, {
        ...row,
        approvalStatus: status
      });
      fetchUsers();
      setSnackbar({ open: true, message: `Kullanıcı ${status} olarak güncellendi`, severity: "success" });
    } catch (err) {
      console.error("Onay hatası:", err);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) return;

    try {
      await axios.delete(`/api/users/${id}`);
      fetchUsers();
      setSnackbar({ open: true, message: "Kullanıcı silindi", severity: "success" });
    } catch (err) {
      console.error("Silme hatası:", err);
      setSnackbar({ open: true, message: "Kullanıcı silinemedi", severity: "error" });
    }
  };

  const handleView = (row) => {
    setFormData(row);
    setPreview(row.avatar);
    setViewMode(true);
    setOpen(true);

    // 🔐 Logları sıfırla ve ilk sayfayı çek
    setLogs([]);
    setLogsCursor(null);
    setLogsHasMore(false);
    fetchLogs(row._id, true);
  };

  const handleEdit = (row) => {
    const { password, ...rest } = row;
    setFormData({ ...rest, password: "" }); // ❗ boş olarak başlat
    setPreview(row.avatar);
    setViewMode(false);
    setOpen(true);
  };

  const handleApprovalChange = async (status) => {
    try {
      await axios.put(`/api/users/${formData._id}`, {
        ...formData,
        approvalStatus: status
      });
      fetchUsers();
      setSnackbar({ open: true, message: `Kullanıcı ${status} olarak güncellendi`, severity: "success" });
      setOpen(false);
    } catch (err) {
      console.error("Onay hatası:", err);
    }
  };

  return (
    <Box p={3}>
      {/* Kartlar */}
      <Box display="flex" justifyContent="center" gap={2} mb={3}>
        {[{
          icon: <DoneIcon color="success" />, label: "Aktif Kullanıcı", value: counts.active
        }, {
          icon: <PersonOutlineIcon color="warning" />, label: "Onay Bekliyor", value: counts.pending
        }, {
          icon: <PeopleAltIcon color="primary" />, label: "Toplam", value: counts.total
        }].map((card, i) => (
          <Box key={i} p={2} boxShadow={2} borderRadius={2} bgcolor="white" width={250} display="flex" flexDirection="column" alignItems="center">
            {card.icon}
            <Typography variant="h6" mt={1}>{card.label}</Typography>
            <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Yeni Kullanıcı Ekle Butonu */}
      <Box display="flex" justifyContent="flex-start" mb={2} mt={6}>
        <Button startIcon={<PersonAddIcon />} variant="contained" onClick={() => {
          setFormData({
            name: "", surname: "", username: "", email: "", phone: "",
            age: "", gender: "", city: "", occupation: "", membershipType: "Free",
            approvalStatus: "Onay Bekliyor", isActive: true, isLocked: false, password: "", role: "user", registeredVia: "Email"
          });
          setViewMode(false);
          setOpen(true);
          setPreview(null);
        }}>
          Yeni Kullanıcı
        </Button>
      </Box>

      {/* Arama & Filtre */}
      <Box display="flex" gap={2} mb={2} alignItems="center" width={"40%"}>
        <TextField label="Ara" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Onay Durumu</InputLabel>
          <Select value={approvalFilter} onChange={(e) => setApprovalFilter(e.target.value)} label="Onay Durumu">
            <MenuItem value="">Tümü</MenuItem>
            <MenuItem value="Onay Bekliyor">Onay Bekliyor</MenuItem>
            <MenuItem value="Onaylandı">Onaylandı</MenuItem>
            <MenuItem value="Reddedildi">Reddedildi</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* DataGrid */}
      <Box sx={{ width: '97%', mb: 2 }}>
        <DataGrid
          rows={users
            .filter(u => u.role === "user")
            .filter(u =>
              u.name.toLowerCase().includes(searchText.toLowerCase()) &&
              (approvalFilter === "" || u.approvalStatus === approvalFilter)
            )}
          getRowId={(row) => row._id}
          autoHeight
          columns={[
            {
              field: "avatar",
              headerName: "Avatar",
              width: 80,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                  <Avatar src={params.row.avatar} alt={params.row.name} />
                </Box>
              )
            },
            { field: "name", headerName: "Ad", flex: 0.6 },
            { field: "surname", headerName: "Soyad", flex: 0.6 },
            { field: "city", headerName: "Şehir", flex: 0.6 },
            { field: "membershipType", headerName: "Üyelik", flex: 0.6 },
            {
              field: "activePackageName",
              headerName: "Aktif Üyelik",
              flex: 1,
              renderCell: (params) => {
                const subDoc = activeSubscriptions[params.row._id];
                const activeSubsArray = (subDoc?.subscriptions || []).filter(s => s.isActive);
                const packageNames = activeSubsArray.map(s => s.packageName).filter(Boolean);
                if (!packageNames.length) {
      return (
        "Yok"
      );
    }
               const firstName = packageNames[0];
    const extraCount = packageNames.length - 1;
    const chipLabel = extraCount > 0 ? `${firstName} +${extraCount}` : firstName;
    const tooltipContent = (
      <Box>
        {packageNames.map((name, idx) => (
          <Typography key={idx} variant="body2">
            {name}
          </Typography>
        ))}
      </Box>
    );

    return (
      <Tooltip
        arrow
        placement="top"
        title={tooltipContent}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              borderRadius: "16px",
              px: 1.5,
              py: 0.5,
              fontSize: "0.75rem",
              fontWeight: 500,
              lineHeight: 1.2,
              maxWidth: "100%",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}
          >
            {chipLabel}
          </Box>
        </Box>
      </Tooltip>
    );
              }
            },

            { field: "approvalStatus", headerName: "Onay", flex: 0.6 },
            {
              field: "isActive",
              headerName: "Durum",
              flex: 0.6,
              renderCell: (params) => (
                <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100%">
                  <Typography color={params.value ? "green" : "gray"}>{params.value ? "Aktif" : "Pasif"}</Typography>
                </Box>
              )
            },
            {
              field: "actions",
              headerName: "İşlemler",
              flex: 1.2,
              renderCell: (params) => (
                <Box display="flex" justifyContent="center" width="100%" height="100%" gap={0.2}>
                  <Tooltip title="Detay">
                    <IconButton fontSize="small" onClick={() => handleView(params.row)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Düzenle">
                    <IconButton fontSize="small" onClick={() => handleEdit(params.row)} >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Sil">
                    <IconButton
                      fontSize="small"
                      onClick={() => handleDeleteUser(params.row._id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {params.row.approvalStatus === "Onay Bekliyor" && (
                    <>
                      <Tooltip title="Onayla">
                        <IconButton
                          fontSize="small"
                          onClick={() => handleApprovalChangeByRow(params.row, "Onaylandı")}
                        >
                          <CheckCircleIcon color="success" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reddet">
                        <IconButton
                          fontSize="small"
                          onClick={() => handleApprovalChangeByRow(params.row, "Reddedildi")}
                        >
                          <CancelIcon color="error" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {["Onaylandı", "Reddedildi"].includes(params.row.approvalStatus) && (
                    <Tooltip title="Onayı Geri Al">
                      <IconButton
                        fontSize="small"
                        onClick={() => handleApprovalChangeByRow(params.row, "Onay Bekliyor")}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )
            }
          ]}
        />
      </Box>

      {/* Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {viewMode ? "Kullanıcı Detayı" : formData._id ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı"}
          <IconButton onClick={() => setOpen(false)} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box mt={2}>
            <Typography fontWeight="bold" mb={1}>Avatar</Typography>
            <Button variant="outlined" component="label" disabled={viewMode}>
              Yükle
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setFormData((prev) => ({ ...prev, avatar: file }));
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </Button>
            {preview && (
              <Box mt={2}>
                <Avatar
                  src={preview || undefined}
                  alt="Avatar Önizleme"
                  sx={{ width: 120, height: 120, mx: 'auto', mt: 2 }}
                />
              </Box>
            )}
          </Box>

          <Typography fontWeight="bold" mt={3} mb={1}>Kullanıcı Bilgileri</Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {Object.keys(fieldConfig).map((key) => (
              <TextField
                key={key}
                name={key}
                label={fieldConfig[key].label}
                type={fieldConfig[key].type || "text"}
                value={formData[key]}
                onChange={(e) => {
                  const val = e.target.value;
                  if (key === "age") {
                    if (/^\d*$/.test(val)) {
                      setFormData({ ...formData, [key]: val });
                    }
                  } else {
                    setFormData({ ...formData, [key]: val });
                  }
                }}
                margin="dense"
                fullWidth
                disabled={viewMode}
                inputProps={fieldConfig[key].inputProps || {}}
              />
            ))}
          </Box>

          {!formData._id && (
            <>
              <Typography fontWeight="bold" mt={3}>Şifre</Typography>
              <TextField
                label="Şifre"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="dense"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </>
          )}
          {formData._id && !viewMode && (
            <>
              <Typography fontWeight="bold" mt={3}>Yeni Şifre (Opsiyonel)</Typography>
              <TextField
                label="Yeni Şifre"
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                margin="dense"
                fullWidth
                placeholder="Şifreyi değiştirmek için girin"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </>
          )}

          <Typography fontWeight="bold" mt={3} mb={1}>Sistem Bilgileri</Typography>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
            {[
              { key: "gender", label: "Cinsiyet", options: ["Erkek", "Kadın", "Belirtmek istemiyorum"] },
              { key: "approvalStatus", label: "Onay Durumu", options: ["Onay Bekliyor", "Onaylandı", "Reddedildi"] },
              { key: "membershipType", label: "Üyelik Tipi", options: ["Free", "Premium", "Enterprise"] },
              { key: "registeredVia", label: "Kayıt Yöntemi", options: ["Email", "Google", "Facebook", "Twitter", "Apple"] },
              { key: "role", label: "Rol", options: ["user"] }
            ].map(({ key, label, options }) => (
              <FormControl fullWidth margin="dense" key={key} disabled={viewMode}>
                <InputLabel>{label}</InputLabel>
                <Select
                  value={formData[key]}
                  label={label}
                  onChange={(e) => setFormData({ ...formData, [key]: e.target.value })}
                >
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ))}
          </Box>

          <Box mt={2}>
            {["isActive", "isLocked"].map((key) => (
              <FormControlLabel
                key={key}
                control={
                  <Switch
                    checked={formData[key]}
                    onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                    disabled={viewMode}
                  />
                }
                label={key}
              />
            ))}
          </Box>

          {viewMode && (
            <Box mt={4}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setLogsOpen(!logsOpen)}
                sx={{ mb: 1 }}
              >
                {logsOpen ? "İşlem Geçmişini Gizle" : "İşlem Geçmişini Göster"}
              </Button>

              {logsOpen && (
                <Box
                  border="1px solid #ddd"
                  borderRadius={2}
                  p={2}
                  bgcolor="#fafafa"
                  maxHeight={260}
                  overflow="auto"
                >
                  {logs.map((log, idx) => (
                    <Box key={log._id || idx} mb={1}>
                      <Typography variant="body2">
                        • {mapActionLabel(log.action)} – <em>{new Date(log.createdAt).toLocaleString()}</em>
                      </Typography>
                      {Array.isArray(log.changes) && log.changes.map((c, i) => (
                        <Typography key={i} variant="body2" sx={{ pl: 2 }} color="text.secondary">
                          {c.field}: <s>{fmtVal(c.from)}</s> → <strong>{fmtVal(c.to)}</strong>
                        </Typography>
                      ))}
                    </Box>
                  ))}

                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Typography variant="caption" color="text.secondary">
                      {logsLoading ? "Yükleniyor..." : logs.length ? `${logs.length} kayıt` : "Kayıt yok"}
                    </Typography>
                    {logsHasMore && (
                      <Button size="small" onClick={() => fetchLogs(formData._id)}>
                        Daha fazla yükle
                      </Button>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          {viewMode && formData.approvalStatus === "Onay Bekliyor" && (
            <>
              <Button color="success" onClick={() => handleApprovalChange("Onaylandı")}>Onayla</Button>
              <Button color="error" onClick={() => handleApprovalChange("Reddedildi")}>Reddet</Button>
            </>
          )}
          {!viewMode && (
            <>
              <Button onClick={() => setOpen(false)}>Vazgeç</Button>
              <Button variant="contained" onClick={handleSubmit}>Kaydet</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default UsersPage;
